import { CompetencySlug, QuestionType } from "../constants";
import { Competency } from "../schemas";

export const decisionMaking: Competency = {
  id: CompetencySlug.DecisionMaking,
  name: "Decision Making",
  definition:
    "The ability to identify the real problem, gather sufficient information, generate and evaluate options, and arrive at a sound, timely, and defensible course of action. Effective decision making in high-trust roles requires balancing urgency with thoroughness, grounding choices in policy and ethics, and accounting for the impact on all affected parties. It also requires owning the decision after the fact — monitoring outcomes, acknowledging errors, and correcting course without deflection.",
  questionTypes: {
    [QuestionType.BEHAVIOURAL]: {
      dimensions: [
        {
          key: "policy-law-ethics",
          label: "Application of Policy / Law / Ethics",
          weight: 0.20,
          anchors: {
            0: "No reference to policy, law, or ethics. Decision is based entirely on personal preference, habit, or convenience.",
            25: "Vague awareness that rules exist, but cannot identify which ones apply or explain their relevance to the decision.",
            50: "References a relevant policy or ethical principle, and it played some role in the decision, but the reasoning is incomplete or superficial.",
            75: "Correctly identifies the applicable policy, law, or ethical standard and explains specifically how it shaped the decision. The decision is demonstrably defensible.",
            100: "All of the above, plus demonstrates nuanced understanding of competing rules or ethical tensions. Can anticipate challenges and articulate precisely why the chosen path holds up to scrutiny.",
          },
          reviewTrigger: {
            atOrBelow: 25,
            reason: "Inability or unwillingness to ground a decision in rules or ethics is a disqualifying trait in a high-trust role. Flag for mandatory review regardless of overall score.",
          },
        },
        {
          key: "judgment-under-pressure",
          label: "Judgment Under Pressure",
          weight: 0.17,
          anchors: {
            0: "Decision-making quality collapses under pressure. Becomes reactive, erratic, or paralyzed. Pressure drives the decision rather than the reasoning.",
            25: "Pressure visibly degrades the reasoning process. Arrives at a decision, but the path there is scattered or inconsistent.",
            50: "Maintains basic decision-making quality under pressure. Acknowledges the pressure but does not allow it to dictate the outcome.",
            75: "Process remains structured and deliberate despite pressure. Demonstrates self-regulation. Calibrates urgency appropriately — neither rushed nor frozen.",
            100: "All of the above, plus pressure appears to activate a more disciplined process. Identifies and names emotional or situational triggers and manages them explicitly.",
          },
        },
        {
          key: "risk-assessment",
          label: "Risk Assessment",
          weight: 0.15,
          anchors: {
            0: "No risk assessment evident. Acts without considering consequences to self, others, or the organization.",
            25: "Identifies one obvious risk but misses others. Assessment is incomplete or driven by the most salient rather than the most significant risk.",
            50: "Identifies the primary risks to key affected parties — self, public, colleagues, organization — and factors them into the decision.",
            75: "Systematically assesses risk across multiple dimensions (safety, legal, reputational, organizational). Weighs both probability and severity. Uses risk assessment to rank options.",
            100: "All of the above, plus identifies non-obvious or second-order risks. Demonstrates proportionality. Risk thinking is integrated throughout the decision, not applied after the fact.",
          },
          reviewTrigger: {
            atOrBelow: 0,
            reason: "Acting with no awareness of consequences to others is a public safety concern. A score of zero on this dimension is a mandatory review trigger.",
          },
        },
        {
          key: "accountability",
          label: "Accountability",
          weight: 0.14,
          anchors: {
            0: "Deflects ownership entirely. Blames circumstances, insufficient information, or other people. No evidence of learning or self-examination.",
            25: "Nominally owns the decision but attributes outcomes primarily to external factors. Self-reflection is shallow and unconvincing.",
            50: "Takes clear ownership of the decision and its outcome. Can describe what went well and what did not. Shows evidence of learning.",
            75: "Takes full ownership including any errors. Describes specific adjustments made or that would be made. Does not over-explain or hedge. Demonstrates follow-through on outcomes.",
            100: "All of the above, plus demonstrates proactive accountability — raises problems before they escalate, invites scrutiny, and holds themselves to a standard regardless of whether anyone is watching.",
          },
          reviewTrigger: {
            atOrBelow: 25,
            reason: "A candidate who deflects ownership entirely represents an integrity risk. Flag for mandatory review.",
          },
        },
        {
          key: "problem-identification",
          label: "Problem Identification",
          weight: 0.10,
          anchors: {
            0: "Identifies the wrong problem entirely, or addresses only surface symptoms. The decision that follows is aimed at the wrong target.",
            25: "Identifies part of the real problem but misses important dimensions. Response is partially misdirected as a result.",
            50: "Correctly identifies the core problem and directs the response appropriately.",
            75: "Identifies the core problem and explicitly distinguishes it from complicating or surface-level factors. Problem framing visibly guides the quality of the decision.",
            100: "All of the above, plus identifies systemic or upstream causes. Recognizes when the presenting problem is a symptom of something larger. Problem framing is both accurate and actionable.",
          },
        },
        {
          key: "stakeholder-consideration",
          label: "Stakeholder Consideration",
          weight: 0.08,
          anchors: {
            0: "Decision reflects awareness of only one party, typically self. No consideration of broader impact.",
            25: "Acknowledges that others are affected but does not factor this into the decision in any meaningful way.",
            50: "Considers the impact on the primary affected parties — public, colleagues, organization — and this is visible in the decision taken.",
            75: "Proactively maps the decision's impact across multiple stakeholders and balances competing interests. Demonstrates awareness of the institutional and community trust dimension.",
            100: "All of the above, plus identifies asymmetric impact — who bears the most risk, who needs communication, who needs protection. Considers long-term relational and organizational consequences.",
          },
        },
        {
          key: "information-gathering",
          label: "Information Gathering",
          weight: 0.06,
          anchors: {
            0: "Acts on assumption or incomplete information without attempting to gather more. Alternatively, gathers information aimlessly without clear purpose.",
            25: "Gathers some relevant information but misses key items or cannot identify what is still missing.",
            50: "Identifies the most important missing information and takes targeted steps to obtain it before acting.",
            75: "Demonstrates clear discipline — knows what is needed, what can be proceeded without, and articulates a clear threshold for action.",
            100: "All of the above, plus demonstrates meta-awareness: explicitly acknowledges what cannot be known and makes a principled decision about how to manage that gap. Does not allow information gathering to become a mechanism for avoiding the decision.",
          },
        },
        {
          key: "consideration-of-options",
          label: "Consideration of Options",
          weight: 0.05,
          anchors: {
            0: "Describes only one option, presented as the obvious or only choice. No deliberation evident.",
            25: "Mentions a second option in passing but does not genuinely evaluate it.",
            50: "Articulates two or more options and provides a clear rationale for the one chosen.",
            75: "Generates multiple options, evaluates them against relevant criteria, and selects with transparent reasoning. Demonstrates the chosen option was not simply the most convenient.",
            100: "All of the above, plus demonstrates creative or non-obvious option generation. Considers hybrid approaches or shows the final choice reflects genuine optimization across competing criteria rather than default.",
          },
        },
        {
          key: "timeliness",
          label: "Timeliness",
          weight: 0.03,
          anchors: {
            0: "Either acts so immediately that no real decision process occurred, or describes delay so significant that the situation deteriorated as a result.",
            25: "Timing was poor but not catastrophic. Decision was either somewhat rushed or somewhat delayed beyond what the situation required.",
            50: "Made the decision within a timeframe appropriate to the situation. Demonstrates awareness that timing is itself a variable.",
            75: "Explicitly calibrates urgency to the situation. Distinguishes which elements require immediate action from those that can wait. Does not conflate activity with urgency.",
            100: "All of the above, plus demonstrates that timeliness was a deliberate variable — not just how fast they moved, but when each component of the response was deployed for maximum effect.",
          },
        },
        {
          key: "outcome-orientation",
          label: "Outcome Orientation",
          weight: 0.02,
          anchors: {
            0: "Cannot articulate what a successful outcome looks like. Describes actions only, with no reference to goals or endpoints.",
            25: "Has a vague sense of what success looks like but it is implicit, not articulated.",
            50: "Can clearly state what a good outcome looks like before or alongside the actions taken.",
            75: "Defines success criteria explicitly and uses them to evaluate options. Checks the actual outcome against those criteria.",
            100: "All of the above, plus success criteria were thoughtfully constructed — considers both short-term and long-term success, and accounts for unintended consequences as part of the definition.",
          },
        },
      ],
    },
    [QuestionType.SITUATIONAL]: {
      topIndicators: [
        "Gather facts before acting",
        "Assess multiple risks and competing priorities",
        "Avoid assumptions and premature conclusions",
        "Apply ethical and professional standards consistently",
        "Communicate appropriately with relevant stakeholders",
        "Make timely, proportionate, and defensible decisions",
        "Demonstrate ownership and follow-through",
        "Clearly articulate the desired outcome and rationale for their actions",
      ],
      scoringNotes: [
        "Evaluate whether the candidate's proposed approach is both operationally sound and ethically grounded. A decisive action may still score low on Ethics, Policy & Professional Standards if the candidate cannot explain why it is defensible. A candidate who says 'I would report it' without being able to articulate the specific obligation at play scores no higher than 50 on that dimension.",
      ],
      dimensions: [
        {
          key: "risk-assessment",
          label: "Risk Assessment",
          weight: 0.25,
          anchors: {
            0: "Fails to recognize any risks associated with the situation. Focuses only on completing their own work or dismisses the concern entirely.",
            25: "Identifies only one obvious risk (e.g., policy violation) while overlooking impacts on safety, quality, integrity, public trust, clients, colleagues, or the organization.",
            50: "Identifies several relevant risks but treats them independently and does not assess likelihood, severity, or competing priorities.",
            75: "Demonstrates a structured assessment of the primary risks. Recognizes uncertainty, potential consequences, and the need to balance competing obligations.",
            100: "Thoroughly assesses immediate and longer-term risks to individuals, service delivery, organizational integrity, public trust, and fairness. Demonstrates nuanced judgment under uncertainty and clearly explains how risk assessment drives subsequent actions.",
          },
          reviewTrigger: {
            atOrBelow: 0,
            reason: "Acting with no awareness of consequences to others is a public safety concern. A score of zero on this dimension is a mandatory review trigger.",
          },
        },
        {
          key: "information-gathering-analysis",
          label: "Information Gathering & Analysis",
          weight: 0.20,
          anchors: {
            0: "Makes assumptions and takes action without seeking additional information.",
            25: "Acknowledges the need for information but gathers only limited facts before reaching conclusions.",
            50: "Seeks relevant information but does not clearly explain how it will be evaluated or verified.",
            75: "Uses a logical process to gather facts, assess credibility, verify information, and distinguish facts from assumptions.",
            100: "Demonstrates a comprehensive, objective, and methodical approach to gathering and evaluating information while remaining impartial and avoiding premature conclusions.",
          },
        },
        {
          key: "decision-making-judgment",
          label: "Decision Making & Judgment",
          weight: 0.20,
          anchors: {
            0: "Avoids making a decision or relies entirely on others to determine the appropriate course of action.",
            25: "Makes a decision based primarily on assumptions, emotions, or incomplete information.",
            50: "Selects a generally reasonable course of action but provides limited rationale or consideration of alternatives.",
            75: "Demonstrates sound judgment by weighing options, considering consequences, and selecting an appropriate course of action.",
            100: "Demonstrates exceptional judgment by systematically evaluating options, balancing competing priorities, and selecting a proportionate, defensible, and well-reasoned course of action.",
          },
        },
        {
          key: "ethics-policy-professional-standards",
          label: "Ethics, Policy & Professional Standards",
          weight: 0.15,
          anchors: {
            0: "Makes no reference to professional obligations, organizational standards, or ethical responsibilities.",
            25: "Recognizes that rules or policies may exist but cannot explain their relevance to the situation.",
            50: "Demonstrates awareness of professional obligations and applies them in a general manner.",
            75: "Clearly integrates ethical considerations, professional standards, and organizational expectations into decision making.",
            100: "Demonstrates a sophisticated understanding of how ethics, policy, fairness, accountability, and organizational values interact when making decisions under uncertainty.",
          },
        },
        {
          key: "communication-stakeholder-management",
          label: "Communication & Stakeholder Management",
          weight: 0.10,
          anchors: {
            0: "Ignores key stakeholders or proposes inappropriate communication.",
            25: "Communicates with only some stakeholders or uses ineffective communication approaches.",
            50: "Identifies relevant stakeholders and provides a reasonable communication strategy.",
            75: "Demonstrates thoughtful communication tailored to stakeholders while maintaining professionalism and confidentiality.",
            100: "Demonstrates highly effective stakeholder management, balancing transparency, confidentiality, professionalism, and organizational needs throughout the decision-making process.",
          },
        },
        {
          key: "accountability-follow-through",
          label: "Accountability & Follow-Through",
          weight: 0.05,
          anchors: {
            0: "Takes no ownership of the issue after initial action.",
            25: "Assumes someone else will resolve the matter.",
            50: "Demonstrates some follow-up but lacks a clear accountability plan.",
            75: "Clearly identifies responsibilities, reporting requirements, and appropriate follow-up actions.",
            100: "Demonstrates strong ownership by ensuring appropriate documentation, follow-up, monitoring, and accountability throughout the process.",
          },
        },
        {
          key: "outcome-orientation",
          label: "Outcome Orientation",
          weight: 0.05,
          anchors: {
            0: "Cannot articulate what a successful outcome would look like.",
            25: "Provides a vague or incomplete description of success.",
            50: "Identifies a reasonable desired outcome.",
            75: "Clearly defines success and explains how actions support that outcome.",
            100: "Defines both immediate and long-term success, considers unintended consequences, and demonstrates how outcomes support safety, integrity, effectiveness, and public trust.",
          },
        },
      ],
    },
  },
};
